import type {
  PurchaseOrderLineInput,
  PurchaseOrderLineSuggestion,
} from "@/modules/inventory/domain/types";
import { nanoid } from "nanoid";
import type { PoCatalogPick } from "./poCatalogTypes";

export type PoDraftLineRow = PurchaseOrderLineInput & { key: string };

export function todayDateString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function newDraftLine(): PoDraftLineRow {
  return {
    key: nanoid(),
    itemType: "INGREDIENT",
    itemId: "",
    qtyOrdered: 1,
    unitCostLak: 0,
    note: null,
  };
}

export function generateReceiveLotNo(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `LOT-${y}${m}${d}-${suffix}`;
}

export function toLinePayload(lines: PoDraftLineRow[]): PurchaseOrderLineInput[] {
  return lines.map(({ key: _k, ...rest }) => rest);
}

export function mergeSuggestedIntoDraftLines(
  current: PoDraftLineRow[],
  picks: PurchaseOrderLineSuggestion[],
): PoDraftLineRow[] {
  const next = [...current];
  for (const s of picks) {
    const dupIdx = next.findIndex(
      (l) => l.itemType === s.itemType && l.itemId === s.itemId,
    );
    if (dupIdx >= 0) {
      const row = next[dupIdx];
      next[dupIdx] = {
        ...row,
        qtyOrdered: row.qtyOrdered + s.suggestedQty,
        unitCostLak: s.unitCostLak || row.unitCostLak,
      };
      continue;
    }
    const emptyIdx = next.findIndex((l) => !l.itemId);
    const row: PoDraftLineRow = {
      key: emptyIdx >= 0 ? next[emptyIdx].key : nanoid(),
      itemType: s.itemType,
      itemId: s.itemId,
      qtyOrdered: s.suggestedQty,
      unitCostLak: s.unitCostLak,
      note: null,
    };
    if (emptyIdx >= 0) next[emptyIdx] = row;
    else next.push(row);
  }
  const filled = next.filter((l) => l.itemId);
  return filled.length > 0 ? filled : [newDraftLine()];
}

export function mergeCatalogPicksIntoDraftLines(
  current: PoDraftLineRow[],
  picks: PoCatalogPick[],
): PoDraftLineRow[] {
  const next = [...current];
  for (const p of picks) {
    const dupIdx = next.findIndex(
      (l) => l.itemType === p.itemType && l.itemId === p.itemId,
    );
    if (dupIdx >= 0) {
      const row = next[dupIdx];
      next[dupIdx] = { ...row, qtyOrdered: row.qtyOrdered + 1 };
      continue;
    }
    const emptyIdx = next.findIndex((l) => !l.itemId);
    const row: PoDraftLineRow = {
      key: emptyIdx >= 0 ? next[emptyIdx].key : nanoid(),
      itemType: p.itemType,
      itemId: p.itemId,
      qtyOrdered: 1,
      unitCostLak: 0,
      note: null,
    };
    if (emptyIdx >= 0) next[emptyIdx] = row;
    else next.push(row);
  }
  const filled = next.filter((l) => l.itemId);
  return filled.length > 0 ? filled : [newDraftLine()];
}
